import { ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { ThemeServiceHelper } from 'common/theme/service.helper';
import { WorkAreaManager } from 'common/workitems/work-area-manager.service';
import { filter } from 'rxjs/operators';
import { LaneManagerService } from 'shared/components/lane-buttons/shared/services/lane-manager/lane-manager.service';
import { WorkItemLaneType } from 'shared/components/lane-buttons/shared/services/lane-manager/work-item-lane-type.enum';
import { IntegrationWorkItemLaneModel, WorkItemLaneModel } from 'shared/components/lane-buttons/shared/services/lane-manager/work-item-lane.model';
import { BrowserSupportService } from 'shared/services/browser-support/browser-support.service';
import { CannedMessageRequest } from 'common/canned-messages/canned-message-request.interface';
import { VisitorInfoModel } from 'shared/models/visitor-info/visitor-info.model';
import { NanorepService } from 'shared/components/nanorep-panel/nanorep.service';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { Settings } from 'common/settings/settings.interface';
import { WorkItemsTrackingService } from 'common/workitems/workitems-tracking.service';
import { Subscription } from 'rxjs';
import { WorkItemEventParams } from 'common/common/constants/analytics-enums';
import { UserPermissionService } from 'shared/services/user-permissions/user-permission.service';
import { DiscussionsService } from 'shared/components/discussions/discussions.service';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { WidgetBase } from 'common/panel-layout';
import { CannedMessagePanelComponent } from 'shared/components/canned-message-panel/canned-message-panel.component';
import { BcVisitorInfoBuilderService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/visitor-info-builder.service';
import { WorkItemObservableCacheService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/work-item-observable-cache.service';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { BcSettingsService } from 'common/settings/settings.service';
import { BcRestApi } from 'common/api/rest-api';
import { BcAuth } from 'common/auth/auth.service';
import { BcConfigService } from 'common/config/provider';
import { SmartAdvisorService } from 'shared/components/smart-advisor/smart-advisor.service';
import { AccountPermissionType } from 'common/common/constants/account-permission-type';

const SCROLL_SMOOTH_ANIMATION_LENGTH = 300;

@Component({
  selector: 'app-chat-widget',
  templateUrl: './chat-widget.component.html',
  styleUrls: ['./chat-widget.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ChatWidgetComponent extends WidgetBase implements OnInit, OnDestroy {
  @Input()
  public workItem;

  private _laneToggledObservable: Subscription;
  private _isFeatureConfigLoaded: boolean = false;
  private _isNanorepEnabled: boolean = false;
  private _hasDiscussions: boolean = false;
  private _toggleExpandedSubscription: Subscription;
  private _trackSelectedItemSubscription: Subscription;
  private _waitForTrack = false;
  private _hasVendorRemoteControl: boolean;

  public operator;
  public isMultiLane: boolean;
  public cannedMessageOpenSubscription: Subscription;
  public cannedMessageCloseSubscription: Subscription;
  public cannedMessageSelectionSubscription: Subscription;
  public chatObservable: Subscription;
  public visitorObservable: Subscription;
  public chat;
  public visitor: VisitorInfoModel;
  public chatThemeClass: string;
  public cannedMessage: CannedMessageRequest;
  public laneObservable: Subscription;
  public workItemLanes: WorkItemLaneModel[];
  public integrationWorkItemLanes;
  public expandedModeTime: number;
  public instantlyExpandedDelay = 100; // ms
  public trackInit = false;
  public isSmartAdvisor: boolean = false;
  public hasAssignmentHistoryPermission: boolean = false;
  public smartAdvisor2Enabled = false;

  constructor(private bcRestApi: BcRestApi,
              private bcAuth: BcAuth,
              bcConfig: BcConfigService, private bcAnalytics: BcAnalytics,
              private bcVisitorInfoBuilderService: BcVisitorInfoBuilderService,
              private _workAreaManager: WorkAreaManager, private laneManager: LaneManagerService,
              @Inject('velocityjs') private velocityjs, private browserSupportService: BrowserSupportService,
              private nanorepService: NanorepService, private _rxEmitter: RxEmitter,
              private smartAdvisorService: SmartAdvisorService,
              private _bcSettingsService: BcSettingsService, private userPermissionService: UserPermissionService,
              private discussionsService: DiscussionsService, private bcWorkItemsTrackingService: WorkItemsTrackingService,
              private _element: ElementRef, renderer: Renderer2,
              private _changeDetector: ChangeDetectorRef,
              private workItemObservableCache: WorkItemObservableCacheService) {
    super(_element.nativeElement, _workAreaManager, renderer, _rxEmitter);

    this.operator = this.bcAuth.getSession();

    this.isMultiLane = bcConfig.current.features.multiLane;

    this.cannedMessageOpenSubscription = null;
    this.cannedMessageCloseSubscription = null;
    this.cannedMessageSelectionSubscription = null;
  }

  ngOnInit() {
    this._initTrackEvents();
    this.loadFeatureValues();
    this.initObservable();
    this.initMessageBus();
    this.initLaneObservable();
    this.initLaneToggledObservable();
    this._checkPermissionForAssignmentHistory();
  }

  ngOnDestroy() {
    if (this.cannedMessageOpenSubscription) {
      this.cannedMessageOpenSubscription.unsubscribe();
      this.cannedMessageOpenSubscription = null;
    }
    if (this.cannedMessageCloseSubscription) {
      this.cannedMessageCloseSubscription.unsubscribe();
      this.cannedMessageCloseSubscription = null;
    }
    if (this.cannedMessageSelectionSubscription) {
      this.cannedMessageSelectionSubscription.unsubscribe();
      this.cannedMessageSelectionSubscription = null;
    }
    if (this.cannedMessageFolderDropdownSubscription) {
      this.cannedMessageFolderDropdownSubscription.unsubscribe();
      this.cannedMessageFolderDropdownSubscription = null;
    }
    if (this._toggleExpandedSubscription) {
      this._toggleExpandedSubscription.unsubscribe();
      this._toggleExpandedSubscription = null;
    }
    if (this._trackSelectedItemSubscription) {
      this._trackSelectedItemSubscription.unsubscribe();
      this._trackSelectedItemSubscription = null;
    }
    if (this.laneObservable) {
      this.laneObservable.unsubscribe();
      this.laneObservable = null;
    }
    if (this._laneToggledObservable) {
      this._laneToggledObservable.unsubscribe();
      this._laneToggledObservable = null;
    }

    this.unregisterPanelCloseListeners();
    this.bcWorkItemsTrackingService.destroyItem(this.workItem.id);

    if (this.chatObservable) {
      this.chatObservable.unsubscribe();
      this.chatObservable = null;
    }

    if (this.visitorObservable) {
      this.visitorObservable.unsubscribe();
      this.visitorObservable = null;
    }

    this.bcVisitorInfoBuilderService.clearCurrentUrlObservable(this.chat.ChatID);
  }

  private _isInstantlyExpanded(): boolean {
    if (this.isExpandedMode()) {
      const expandedModeTime = this.expandedModeTime || 0;
      return Date.now() - expandedModeTime < this.instantlyExpandedDelay;
    } else {
      return false;
    }
  }

  private _initTrackEvents(): void {
    this._toggleExpandedSubscription = this._rxEmitter.subscribe(MessageBusEvents.ToggleExpandedMode, () => {
      this.expandedModeTime = Date.now();
    });

    this._trackSelectedItemSubscription = this.bcWorkItemsTrackingService.onChange.pipe(filter(id => id === this.workItem.id)).subscribe(() => {
      if (!this.isExpandedMode() && this.trackInit || !this._isInstantlyExpanded() && this.isExpandedMode() && this.trackInit) {
        this._trackSelectedEvent();
      } else {
        this._waitForTrack = true;
      }
    });
  }

  private _trackSelectedEvent(): void {
    this._waitForTrack = false;
    this.bcWorkItemsTrackingService.track(this.workItem, {
      [WorkItemEventParams.SMART_ADVISOR_VISIBLE]: this._isSmartAdvisorVisible()
    });
  }

  private _checkPermissionForAssignmentHistory(): void {
    this.hasAssignmentHistoryPermission = this.userPermissionService.hasAccountPermissionFor(AccountPermissionType.AssignmentHistory);
  }

  get isCannedMessagesLaneVisible(): boolean {
    return this._isLaneVisible(WorkItemLaneType.CannedMessage);
  }

  get isSmartAdvisorLaneVisible(): boolean {
    return this._isLaneVisible(WorkItemLaneType.SmartAdvisor);
  }

  get isVisitorInfoLaneVisible(): boolean {
    return this._isLaneVisible(WorkItemLaneType.VisitorInfo);
  }

  get isDiscussionsLaneVisible(): boolean {
    return this._isLaneVisible(WorkItemLaneType.Discussions);
  }

  get isVisitorHistoryLaneVisible(): boolean {
    return this._isLaneVisible(WorkItemLaneType.VisitorHistory);
  }

  isEmbeddedIntegration(workItemLane: WorkItemLaneModel): boolean {
    return workItemLane.type === WorkItemLaneType.EmbeddedIntegration;
  }

  isSalesforceIntegration(workItemLane: WorkItemLaneModel): boolean {
    return workItemLane.type === WorkItemLaneType.SalesforceIntegration;
  }

  isLaneIntegration(workItemLane: WorkItemLaneModel): boolean {
    return this.isEmbeddedIntegration(workItemLane) || this.isSalesforceIntegration(workItemLane);
  }

  isExpandedMode(): boolean {
    return this._workAreaManager.isExpandedMode() || !this.workItem.isCollapsible;
  }

  public isDiscussionsVisible(): boolean {
    return this.isExpandedMode() && this.isDiscussionsLaneVisible && this._hasDiscussions;
  }

  _isLaneVisible(laneType: WorkItemLaneType): boolean {
    // Lane visibility togglers used only in expanded mode
    if (!this.isExpandedMode()) {
      return true;
    }

    const lane = this.workItemLanes && this.workItemLanes.find(workItemLane => workItemLane.type === laneType);
    return lane && lane.visible;
  }

  initObservable(): void {
    this.resetState();

    this.initDataForChat();
  }

  initMessageBus(): void {
    this.cannedMessageOpenSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.ActivateCannedMessagePanel}${this.workItem.id}`, (value) => {
      this.showAndOpenCannedMessagePanel(value.isSmartAdvisor);
    });

    this.cannedMessageCloseSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.CloseCannedMessagePanel}${this.workItem.id}`, () => {
      this.closeCannedMessagePanel();
    });

    this.cannedMessageSelectionSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.SendCannedMessageToEditor}${this.workItem.id}`, (args) => {
      if (args.eventSource === CannedMessagePanelComponent.name) {
        this.closeCannedMessagePanel();
      }
    });
  }

  initLaneObservable(): void {
    this.laneObservable = this.laneManager.getLaneObservable(this.workItem.id)
      .subscribe(lanes => {
        this.workItemLanes = lanes;

        if (this._waitForTrack) {
          this._trackSelectedEvent();
        }

        this.integrationWorkItemLanes = this.workItemLanes
          .filter(lane => this.isLaneIntegration(lane));
      });
  }

  initLaneToggledObservable(): void {
    this._laneToggledObservable = this.laneManager.getLaneToggledObservable(this.workItem.id)
      .subscribe(toggledLane => {
        if (!toggledLane.visible || !this.isLaneIntegration(toggledLane)) {
          return;
        }

        this._changeDetector.detectChanges();
        this._scrollToLane(<IntegrationWorkItemLaneModel> toggledLane);
      });
  }

  resetState(): void {
    this.chat = null;
    this.visitor = null;
    if (this.workItem && this.workItem.id) {
      this.chatThemeClass = ThemeServiceHelper.getCssClass(this.workItem.id);
    } else {
      this.chatThemeClass = 'theme-1';
    }
  }

  initDataForChat(): void {
    if (this.chatObservable) {
      this.chatObservable.unsubscribe();
    }

    if (this.visitorObservable) {
      this.visitorObservable.unsubscribe();
    }

    const workItemId = this.getActualWorkItemId();
    this.chatObservable = this.workItemObservableCache.getWorkItemObservable(workItemId, WorkItemTypes.CHAT).subscribe(chat => {
      this.setChat(chat);
      this._changeDetector.detectChanges();
    });

    this.visitorObservable = this.bcVisitorInfoBuilderService.observeVisitorData(workItemId, WorkItemTypes.CHAT).subscribe(visitorData => {
      this.visitor = visitorData;
      this._changeDetector.detectChanges();
    });
  }

  getActualWorkItemId(): string {
    return this.workItem.id;
  }

  reloadIFrameUrlFromServer(integrationLane: IntegrationWorkItemLaneModel): void {
    this.laneManager.updateIntegrationUrl(this.workItem.id, integrationLane, this.workItem.type);
  }

  setChat(chat): void {
    this._observeActiveAssistStatusChange(chat);
    this.chat = chat;
    this.getChatClass();
    this.cannedMessage = {
      type: bcEnums.cannedMessageTypes.CHAT,
      id: this.chat.ChatID,
      chatWindowDefId: this.chat.ChatWindowDefID,
      language: this.chat.LanguageCode
    };
  }

  _observeActiveAssistStatusChange(chat): void {
    if (!this.browserSupportService.isAssistedBrowsingSupportedByCurrentBrowser()) {
      return;
    }

    if (!this.chat && chat.ActiveAssistStatus === 1) {
      this.bcRestApi.getAssistedBrowsingId(chat.ChatID)
        .then(response => {
          if (response.id) {
            this.reviveAssistedBrowsing();
          }
        });
    } else if (this.chat && this.chat.ActiveAssistStatus === 1 && chat.ActiveAssistStatus !== 1) {
      this.cancelAssistedBrowsing();
    }
  }

  getChatClass(): void {
    this.chatThemeClass = ThemeServiceHelper.getCssClass(this.workItem.id);
  }

  isChatActive(): boolean {
    return this.chat && this.chat.isConversationActive(this.operator);
  }

  _sendCannedMessagePanelAnalytics(action): void {
    this.bcAnalytics.eventTrack('ChatCMPanel', { action });
  }

  private _scrollToLane(lane: IntegrationWorkItemLaneModel): void {
    const multiLaneWrapperElement = this._element.nativeElement.querySelector('.multilane-wrapper');
    const laneElement = multiLaneWrapperElement.querySelector(`[data-integration-id="${lane.id}"]`);

    if (laneElement) {
      this.velocityjs(laneElement, 'scroll', {
        axis: 'x',
        duration: SCROLL_SMOOTH_ANIMATION_LENGTH,
        container: multiLaneWrapperElement
      });
    } else {
      window.requestAnimationFrame(() => {
        this._scrollToLane(lane);
      });
    }
  }

  showAndOpenCannedMessagePanel(isSmartAdvisor: boolean = false): void {
    if (!this.isCannedMessagesLaneVisible) {
      this.laneManager.toggleLane(this.workItem.id, WorkItemLaneType.CannedMessage.toString(), this.workItem.type);
    }
    this.isSmartAdvisor = isSmartAdvisor;
    this.openCannedMessagePanel();
  }

  stopRCSession(): void {
    return this.workItem.stopRCSession();
  }

  cancelAssistedBrowsing(): void {
    this.workItem.stopAssistedBrowsing();
  }

  reviveAssistedBrowsing(): void {
    this.workItem.startAssistedBrowsing();
  }

  onNanorepPanelClose(): void {
    this._rxEmitter.send(`${MessageBusEvents.CloseCannedMessagePanel}${this.workItem.id}`, null);
  }

  doesCustomerUseNanorep(): boolean {
    return this.isNanorepEnabled() && this.workItem.NanorepConfigID;
  }

  isNanorepEnabled(): boolean {
    return this._isNanorepEnabled;
  }

  isFeatureConfigLoaded(): boolean {
    return this._isFeatureConfigLoaded;
  }

  isDiscussionPanelOpened(): boolean {
    let isOpened: boolean = false;

    if (this.workItemLanes) {
      let discussionLane = this.workItemLanes.find((lane) => lane.id === WorkItemLaneType.Discussions.toString());
      isOpened = discussionLane ? discussionLane.visible : false;
    }

    return isOpened;
  }

  async loadFeatureValues(): Promise<void> {
    [
      this._isNanorepEnabled,
      this._hasDiscussions,
      this._hasVendorRemoteControl
    ] = await Promise.all([
        this.nanorepService.hasNanorepFeature(),
        this._hasDiscussionsFeature(),
        this._hasVendorRemoteControlFeature()
      ]);

    this.trackInit = true;

    if (this._waitForTrack && !this.isExpandedMode()) {
      this._trackSelectedEvent();
    }

    this._isFeatureConfigLoaded = true;
    this.smartAdvisor2Enabled = await this.smartAdvisorService.getSmartAdvisorVersionValue();
  }

  public get showActiveAssistPanel(): boolean {
    return !this.workItem.isEnded &&
      this.workItem.hasActiveAssistedSession &&
      (!this.workItem.hasActiveRemoteControl || this.showRemoteControlPanel);
  }

  public get showRemoteControlPanel(): boolean {
    return this.workItem.hasActiveRemoteControl && this._hasVendorRemoteControl === false;
  }

  public get showAssistedBrowsingPanel(): boolean {
    return this.workItem.hasActiveAssistedBrowsing;
  }

  private async _hasDiscussionsFeature(): Promise<boolean> {
    const settings: Settings = await this._bcSettingsService.getSettings();
    return this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_DISCUSSIONS) && this._isDiscussionsEnabled() && this._hasDiscussionPermission();
  }

  private async _hasVendorRemoteControlFeature(): Promise<boolean> {
    const settings: Settings = await this._bcSettingsService.getSettings();
    return this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.RC_NEW_REMOTE_CONTROL);
  }

  private _isDiscussionsEnabled(): boolean {
    return this.discussionsService.isDiscussionsEnabled();
  }

  private _hasDiscussionPermission(): boolean {
    return this.discussionsService.hasDiscussionPermission(this.workItem.type);
  }

  private _isSmartAdvisorVisible(): boolean {
    return this.isChatActive() && this.isCannedMessagesLaneVisible || false;
  }

  public isSmartAdvisorPanelVisible(): boolean {
    const smartAdvisorPanelVisible = this.isFeatureConfigLoaded() && this.doesCustomerUseNanorep() && this.isSmartAdvisorLaneVisible;
    return Boolean(this.smartAdvisor2Enabled ? smartAdvisorPanelVisible : (smartAdvisorPanelVisible && this.isChatActive()));
  }
}
